// Wire types shared across features. Feature-specific shapes live in each
// feature's `api.ts`. Money is always a decimal string ("1250000.000").

export type Money = string;
/** 'YYYY-MM-DD' */
export type IsoDate = string;
/** 'YYYY-MM' */
export type IsoMonth = string;
/** ISO-8601 UTC timestamp */
export type Timestamp = string;

export type Country = 'IQ' | 'LY';
export type Currency = 'IQD' | 'LYD';

export type Role = 'OWNER' | 'MANAGER' | 'ACCOUNTANT' | 'TEACHER' | 'STAFF' | 'PARENT' | 'STUDENT';
export const OFFICE_ROLES: readonly Role[] = ['OWNER', 'MANAGER', 'ACCOUNTANT'];
/** Roles that use the web dashboard: the office, and teachers for attendance. */
export const WEB_ROLES: readonly Role[] = [...OFFICE_ROLES, 'TEACHER'];

export type FeatureKey =
  | 'fees'
  | 'payroll'
  | 'advances'
  | 'staffAttendance'
  | 'timetable'
  | 'examsGrades'
  | 'reportCards'
  | 'homework'
  | 'studentAttendance'
  | 'studentAccounts'
  | 'homeworkSubmissions'
  | 'studentsSeeFees'
  | 'teacherNotes'
  | 'announcements'
  | 'messaging';

export interface FeatureView {
  key: FeatureKey;
  enabled: boolean;
  switchedBy: 'platform' | 'school';
  needs: FeatureKey[];
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
}

export interface MembershipSchool {
  id: string;
  name: string;
  country: Country;
  currency: Currency;
  locale: string;
  logoUrl: string | null;
  primaryColor: string | null;
}

export interface Membership {
  id: string;
  role: Role;
  school: MembershipSchool | null;
}

export interface SessionUser {
  id: string;
  name: string;
  phone: string;
  isPlatformAdmin: boolean;
}

export interface SessionView {
  tokens: TokenPair;
  user: SessionUser;
  memberships: Membership[];
  currentMembershipId: string | null;
}

/** Who did something, next to the bare id the API also sends. */
export interface UserRef {
  id: string;
  name: string;
}

export interface MeView {
  userId: string;
  user?: { id: string; name: string; phone: string };
  isPlatformAdmin: boolean;
  currentMembershipId: string | null;
  role: Role | null;
  memberships: Membership[];
  features: FeatureView[] | null;
}

export interface AcademicYear {
  id: string;
  name: string;
  startDate: Timestamp;
  endDate: Timestamp;
  isCurrent: boolean;
}

export type SchoolStatus = 'TRIAL' | 'ACTIVE' | 'READ_ONLY' | 'SUSPENDED';

/**
 * NONE: no end date (free pilot). OK: more than 14 days left. ENDING: the last
 * 14 days. GRACE: ended, read-only after 7 days. EXPIRED: past those days.
 */
export type SubscriptionState = 'NONE' | 'OK' | 'ENDING' | 'GRACE' | 'EXPIRED';

export interface SubscriptionView {
  plan: string | null;
  trialEndsAt: IsoDate | null;
  paidUntil: IsoDate | null;
  /** Last day of the current period (paid-until, else the trial's end). */
  endsOn: IsoDate | null;
  /** First read-only day if nothing is paid. */
  readOnlyFrom: IsoDate | null;
  /** Negative once ended. */
  daysLeft: number | null;
  state: SubscriptionState;
}

export interface SchoolProfile {
  id: string;
  name: string;
  nameEn: string | null;
  country: Country;
  currency: Currency;
  locale: string;
  timeZone: string;
  logoUrl: string | null;
  primaryColor: string | null;
  status: SchoolStatus;
  roundingUnit: Money;
  absenceDayDivisor: number;
  studentLoginFromGrade: number | null;
  currentYear: AcademicYear | null;
  /** Owner, manager and accountant only. */
  subscription?: SubscriptionView;
  settings?: { weekDays?: number[] } | null;
}

export interface Page<T> {
  total: number;
  items: T[];
}

export type PaymentMethod = 'CASH' | 'CARD' | 'TRANSFER' | 'CHEQUE' | 'WALLET';
export const PAYMENT_METHODS: readonly PaymentMethod[] = ['CASH', 'CARD', 'TRANSFER', 'CHEQUE', 'WALLET'];
export type PaymentStatus = 'PENDING' | 'CLEARED' | 'BOUNCED' | 'REVERSED';
export type CashAccountType = 'CASH_DESK' | 'BANK' | 'WALLET';

export interface CashAccount {
  id: string;
  name: string;
  type: CashAccountType;
  currency: Currency;
  isDefault: boolean;
  active: boolean;
}

export interface GradeRef {
  id?: string;
  number: number;
  nameAr: string;
  nameEn?: string | null;
}
